import {
  Component,
  EventEmitter,
  Inject,
  Input,
  LOCALE_ID,
  OnInit,
  Output,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';

import { StatusPillComponent } from '../../shared/status-pill/status-pill.component';
import { slugify } from '../../shared/testid/testid.util';
import { ProductVm } from '../list-grouping.util';
import { PILL_MAP } from '../product-status.util';
import {
  resolveProductProgress,
  ProductProgress,
  ProductStage,
  StageKey,
} from './product-progress.util';
import { ProposalService } from '../../services/proposal.service';
import { SiteService } from '../../services/site.service';
import { ProjectService } from '../../services/project.service';
import { ModalService } from '../../services/modal.service';
import { CommonUtilityServiceService } from '../../services/common-utility-service.service';
import { getStoredUser } from '../../helpers/user-storage.util';
import { SignedAtpSiteVisit } from '../../model/signed-atp-site-visit';
import { MatTooltipModule } from '@angular/material/tooltip';
import { ProjectActionLockService, PRODUCT_LOCKED_REASON } from '../../services/project-action-lock.service';

// Existing pre-award / award ACTION dialogs — triggered functionally here (their
// visual migration is Spec 8). Reused unmodified so the quote / accept / decline /
// ATP-sign flows behave exactly as the legacy `proposal-details` page did. The
// post-write CONFIRMATION dialogs (offer-accepted, decline-success,
// atp-sign-accepted) are hosted at the page level via ModalService instead — a
// refresh destroys this component, so an inline confirmation would never render.
import { BidDialogComponent } from '../../bid-dialog/bid-dialog.component';
import { DeclineDialogComponent } from '../../decline-dialog/decline-dialog.component';
import { SignAtpDialogComponent } from '../../sign-atp-dialog/sign-atp-dialog.component';
import { AcceptOfferedProposalDialogComponent } from '../../accept-offered-proposal-dialog/accept-offered-proposal-dialog.component';
// Task B4 (counter-offer, docs/connect/2026-08-29-post-visit-completion-and-
// counter-offer-spec.md) — offered from OFFERED and, as a second round, from
// RE_OFFERED once staffing has answered (docs/connect/2026-09-01-staffing-
// counter-back-spec.md). Same "reused dialog, page owns the write" shape as
// decline.
import {
  CounterOfferDialogComponent,
  CounterOfferSubmission,
} from '../../counter-offer-dialog/counter-offer-dialog.component';

// Task 3 (Project History) — reused unmodified for the read-only Documents
// affordance, exactly as the legacy `project-history-table-row` used it.
import { DocumentsDialog } from '../../documents-dialog/documents-dialog.component';

export const SUBMIT_QUOTE_DISABLED_REASON = 'Enter a price and a complete-by date to submit this quote.';

// CONNECT 2.0 list screens (Spec 4, Task 2) — the atomic actionable unit: one
// service line-item on a site. Renders a StatusPill + product title + the
// status-driven action matrix. The action host reproduces the legacy
// `proposal-details` flows exactly (same `ProposalService`/`SiteService` calls,
// same `ProjectService` refresh), but inline on the card instead of a
// click-through detail page (Approach A). Dialogs are triggered, not migrated
// (Spec 8 owns their restyle).
@Component({
  standalone: true,
  selector: 'app-product-line',
  imports: [
    CommonModule,
    FormsModule,
    StatusPillComponent,
    BidDialogComponent,
    DeclineDialogComponent,
    SignAtpDialogComponent,
    AcceptOfferedProposalDialogComponent,
    CounterOfferDialogComponent,
    DocumentsDialog,
    MatTooltipModule,
  ],
  templateUrl: './product-line.component.html',
  styleUrl: './product-line.component.scss',
})
export class ProductLineComponent implements OnInit {
  readonly lockedReason = PRODUCT_LOCKED_REASON;
  readonly submitQuoteDisabledReason = SUBMIT_QUOTE_DISABLED_REASON;

  // Whether a write on this row's own product is still running. Writes on
  // sibling products of the same project do not disable this row.
  get productLocked(): boolean {
    return this.lock.isLocked(this.product?.proposal?.id);
  }

  // The product view-model (see list-grouping.util.ts). Required — every
  // product line is rendered from a grouped SiteDetail.
  @Input({ required: true }) product!: ProductVm;

  // Screen context: 'invitations' | 'proposals' | 'active'. The action variant
  // is driven off the proposal status, not this; `type` only distinguishes the
  // Active read-variant deep-links (Task 6).
  @Input() type: string = '';

  // Parent identifiers, carried for testids (Task 8) and deep-links (Task 6).
  @Input() projectId: string = '';
  @Input() siteNumber: number | null = null;

  // Deduped testid base computed by the parent `site-card` across all of the
  // site's products (see `SiteCardComponent.productTestIds`). Falls back to
  // the self-computed id below when unset (e.g. in isolation/unit tests) —
  // that fallback has no visibility into sibling products, so it cannot
  // dedupe a same-site title collision on its own.
  @Input() baseTestId: string = '';

  // Task 3 (Project History): read-only mode. When true, the entire
  // status-driven action matrix (+ its action dialogs) is suppressed —
  // replaced by a plain due-date/bid-amount summary and a Documents
  // affordance (the 1.0 `info-table-item`'s inline Documents icon, minus
  // Notes/Expenses, which stay hidden per the spec). Defaults false so every
  // existing consumer (Invitations/Pending/Active) is unaffected.
  @Input() readOnly: boolean = false;

  // The site visits an ATP signature from this row left schedulable. Raised to
  // the page that hosts `atp-sign-accepted-dialog` (product-line → site-card →
  // project-group → page) so the confirmation can offer to schedule them.
  // Emitted on EVERY signature, the empty list included, so a second sign
  // cannot inherit the first one's visits.
  @Output() atpSigned = new EventEmitter<SignedAtpSiteVisit[]>();

  // Statuses that render the Active read-variant (status pill + progress +
  // report-writer link). Everything post-award other than the not-awarded set.
  private static readonly ACTIVE_STATUSES = new Set([
    'ACTIVE',
    'ADDRESSING_COMMENTS',
    'IN_REVIEW',
    'REVIEW_PENDING',
    'REVISION_REQUESTED',
    'REVISION_PENDING',
    'COMPLETED',
  ]);

  // Quote-form state (INVITATION). Local so typing doesn't re-render the row;
  // persisted as a draft via ProposalService.saveProposal on blur, mirroring
  // the legacy proposal-details footer inputs.
  bidAmount: number = 0;
  turnTime: number = 0;

  // The values last actually handed to `ProposalService.saveProposal`, seeded
  // in ngOnInit from the loaded proposal. The blur guards below compare
  // against THESE and not against `product.proposal.bid` /
  // `.proposedTurnTime`, which is what they used to read.
  //
  // The reason is the on-input cache mirror in `onPriceChange` /
  // `onTurnTimeChange`: `patchProposalDraftInCache` reaches the SAME proposal
  // object this row's view-model holds (`list-grouping.util.ts` aliases
  // `siteDetail.proposal` onto the ProductVm — it does not copy it), so by
  // blur time the typed value already sits on `product.proposal`. A guard
  // reading it would therefore be false on every blur and the draft would
  // silently never reach the server: it would survive a navigate-away and
  // return (the cache replays) but be lost on a reload.
  private persistedBid: number = 0;
  private persistedTurnTime: number = 0;
  isFixedPriced: boolean = false;
  isDesiredByDate: boolean = false;
  desiredByDate: Date = new Date();
  proposedDeliverDate: any = null;
  submitBidDisabled: boolean = true;

  // Address / service context for the reused dialogs.
  address: any = { streetAddress: '', cityStateZip: '' };
  siteType: string = '';
  siteSubType: string = '';

  // ACTION dialog toggles (mirror proposal-details). The post-write confirmation
  // toggles live on ModalService now (page-hosted) — see the write handlers.
  bidActive: boolean = false;
  isOfferAcceptedActive: boolean = false;
  isDeclineActive: boolean = false;
  signAtpActive: boolean = false;
  isAtp: boolean = false; // drives decline-dialog copy + decline-success isAtp
  isCounterActive: boolean = false; // Task B4 — Counter Price / Counter Again

  // Task 3 (Project History) read-only Documents affordance — combined
  // site + proposal documents (same sourcing as the legacy
  // `project-history-table-row`), and the dialog's open/close toggle.
  readOnlyDocuments: any[] = [];
  readOnlyDocumentsActive: boolean = false;

  // ATP artefacts (generated on demand before signing).
  atpDocument: any = null;
  atpDocumentPath: SafeResourceUrl = '';
  childModalHeaderTitle: string = 'ATP Signed & Project Active';
  childModalBody: string =
    'You have signed the ATP and the project can be viewed from the Active tab.';

  // ProjectDetail snapshot handed to the bid / accept dialogs on open (built
  // from the full raw project + the untouched siteDetail — see buildProjectDetail).
  writeProjectDetail: any = null;

  // The full, untouched raw project object (banner-level fields the write
  // payloads carry: projectOrderId, tableA, specialQuoteRequirements, rush, …),
  // threaded down from the page via project-group → site-card as an @Input so
  // the reused dialogs / ATP generator receive the same ProjectDetail shape the
  // legacy detail page sent — built synchronously, no per-row lookup.
  @Input() rawProject: any = null;
  private user = getStoredUser();

  constructor(
    private proposalService: ProposalService,
    private siteService: SiteService,
    private projectService: ProjectService,
    private modalService: ModalService,
    private utilityService: CommonUtilityServiceService,
    private toastr: ToastrService,
    private sanitizer: DomSanitizer,
    private lock: ProjectActionLockService,
    @Inject(LOCALE_ID) public locale: string
  ) {}

  ngOnInit(): void {
    const site = this.product?.site ?? {};
    const proposal = this.product?.proposal ?? {};

    this.siteType = site.siteType ?? '';
    this.siteSubType = site.siteSubType ?? '';
    this.address = this.utilityService.formatAddress(this.product.siteDetail);

    this.isFixedPriced = !!proposal.fixedAmount;
    this.bidAmount = proposal.bid ?? 0;
    this.turnTime = proposal.proposedTurnTime ?? 0;
    // Same `?? 0` coercion as the two above, so a blur that changed nothing
    // still compares equal and still skips the server write.
    this.persistedBid = this.bidAmount;
    this.persistedTurnTime = this.turnTime;
    this.proposedDeliverDate = proposal.proposedDeliverDate ?? null;
    if (site.desiredByDate) {
      this.desiredByDate = site.desiredByDate;
      this.isDesiredByDate = true;
    }
    this.recomputeSubmitDisabled();
    this.readOnlyDocuments = ProductLineComponent.combineDocuments(
      this.product?.siteDetail
    );
  }

  // ---------------------------------------------------------------------------
  // Status helpers (drive the action matrix).
  // ---------------------------------------------------------------------------
  private get status(): string {
    return (this.product?.status ?? '').toUpperCase();
  }

  get isInvitation(): boolean {
    return this.status === 'INVITATION';
  }
  get isOffered(): boolean {
    return this.status === 'OFFERED';
  }
  // The PA's own counter is still outstanding — staffing has not answered it
  // yet. Deliberately does NOT include RE_OFFERED: once staffing answers, the
  // row is a live offer again, not a submitted counter, and rendering it as
  // "awaiting staffing" would tell the PA the opposite of the truth.
  get isCountered(): boolean {
    return this.status === 'COUNTERED';
  }
  // Staffing answered the counter with a revised fee. `bid` now carries
  // staffing's answer and `counterFee` the PA's previous ask — see
  // `quotedAmount` below.
  get isReOffered(): boolean {
    return this.status === 'RE_OFFERED';
  }
  // The counter action (Counter Price / Counter Again) is offered from
  // exactly these two statuses — the same COUNTERABLE_STATUSES set
  // ProposalHandler.counterOwnedOffer enforces server-side — and from no
  // other status. COUNTERED is deliberately absent: one counter per offer,
  // and the next move on a standing counter is staffing's. A re-offer
  // reopens countering.
  get canCounter(): boolean {
    return this.isOffered || this.isReOffered;
  }
  get isSubmitted(): boolean {
    return ['SUBMITTED', 'PROPOSAL', 'QUOTED'].includes(this.status);
  }
  get isAwarded(): boolean {
    return this.status === 'BID_WON';
  }
  get isNotAwarded(): boolean {
    return ['BID_LOST', 'NO_RESPONSE'].includes(this.status);
  }
  get isActiveVariant(): boolean {
    return ProductLineComponent.ACTIVE_STATUSES.has(this.status);
  }

  // ---------------------------------------------------------------------------
  // StatusPill config.
  // ---------------------------------------------------------------------------
  private get pillConfig(): { key: string; label: string } {
    return (
      PILL_MAP[this.status] ?? {
        key: '',
        label: this.status || 'Unknown',
      }
    );
  }
  get pillStatus(): string {
    return this.pillConfig.key;
  }
  get pillLabel(): string {
    return this.pillConfig.label;
  }

  // ---------------------------------------------------------------------------
  // Active-variant read fields.
  // ---------------------------------------------------------------------------
  // Status/timestamp-derived progress stepper (replaces the dormant
  // `proposal.progress`/`percentComplete` percent bar — neither field is ever
  // populated by order-service/connect-service). Memoized per `product` identity
  // so the template's `*ngFor` over `stages` keeps stable references across
  // change detection (no per-tick DOM churn); recomputed only when the row's
  // product changes.
  //
  // Product identity alone is NOT a sufficient key. The site-visit summary can
  // be refreshed onto the same `ProductVm`/`SiteDetail` object (a post-action
  // merge emission replaces the row's contents in place), and a stepper that
  // ignored that would keep showing "Schedule site visit" after the visit was
  // scheduled — the same class of staleness this stepper was just fixed for. So
  // the key also carries the visit's `status`, which is the only site-visit
  // input `resolveProductProgress` reads.
  private progressFor: ProductVm | null = null;
  private progressVisitStatusFor: string | null = null;
  private progressCache: ProductProgress | null = null;
  get progress(): ProductProgress {
    const visitStatus = this.product?.siteVisit?.status ?? null;
    if (
      this.progressCache === null ||
      this.progressFor !== this.product ||
      this.progressVisitStatusFor !== visitStatus
    ) {
      this.progressFor = this.product;
      this.progressVisitStatusFor = visitStatus;
      this.progressCache = resolveProductProgress(
        this.product?.proposal,
        this.product?.site,
        this.product?.siteVisit
      );
    }
    return this.progressCache;
  }

  // Stable identity for the stepper `*ngFor` so segments aren't rebuilt per tick.
  trackStage = (_: number, stage: ProductStage): StageKey => stage.key;

  // Cosmetic stage-3 hint (Report). Boundaries are untouched — this only
  // labels the "current" stage differently by product type while the report
  // stage is in progress: non-survey products author via Quire (report-writer
  // link), survey products deliver via document upload (no Quire link exists).
  get stageHint(): string | null {
    const reportStage = this.progress.stages.find(
      (stage) => stage.key === 'report' && stage.state === 'current'
    );
    if (!reportStage) {
      return null;
    }
    if (this.product?.site?.serviceType === 'NEXUS_ALTA_SURVEY') {
      return 'Awaiting document upload';
    }
    return this.reportWriterUrl ? 'Report link ready' : null;
  }

  // Report-writer (Quire) deep-link. Task 6 investigated: no
  // reportWriterUrl/quireUrl (or similarly named) field exists on the
  // proposal/product anywhere in this repo's data model or services today, so
  // this stays a defensive read — renders the real link once order-service /
  // connect-service start sending it, "pending" (no fabricated URL) until then.
  get reportWriterUrl(): string | null {
    return this.product?.proposal?.reportWriterUrl ?? null;
  }

  // ---------------------------------------------------------------------------
  // Task 3 (Project History) — read-only completed summary + Documents.
  // ---------------------------------------------------------------------------
  // Mirrors the legacy `project-history-table` row's `deliveryDate`/`bidAmount`
  // computation exactly (proposedDeliverDate falling back to
  // deliverableDueDate; bid straight off the proposal).
  get completedDueDate(): any {
    return (
      this.product?.proposal?.proposedDeliverDate ??
      this.product?.proposal?.deliverableDueDate ??
      null
    );
  }
  get completedBidAmount(): number {
    return this.product?.proposal?.bid ?? 0;
  }

  toggleReadOnlyDocumentsModal(): void {
    this.readOnlyDocumentsActive = !this.readOnlyDocumentsActive;
  }

  // The dialog emits the updated siteDetail after an upload/delete — recompute
  // the combined list so the Documents badge count stays in sync.
  onReadOnlyDocumentsUpdate(event: { siteDetail: any }): void {
    this.readOnlyDocuments = ProductLineComponent.combineDocuments(
      event.siteDetail
    );
  }

  private static combineDocuments(siteDetail: any): any[] {
    const documents: any[] = [];
    if (siteDetail?.site?.documents) {
      documents.push(...siteDetail.site.documents);
    }
    if (siteDetail?.proposal?.documents) {
      documents.push(...siteDetail.proposal.documents);
    }
    return documents;
  }

  // Read-line for the SUBMITTED/QUOTED "awaiting award" summary. Also doubles
  // as the "Offered fee" figure on OFFERED/COUNTERED/RE_OFFERED (mock B1/B3)
  // — `bid` always carries the terms staffing offered, and a PA counter never
  // touches it (see ProposalHandler.counterOwnedOffer: bid stays the offered
  // price so the workspace can always show the PA's counter against it).
  //
  // On RE_OFFERED it is staffing's REVISED fee: the counter-back writes a new
  // `bid` (ProposalHandler.processExpenseUpdateFromOrderTracker) while
  // `counterFee` is left standing as the PA's previous ask. So the two
  // figures swap roles between COUNTERED and RE_OFFERED — on COUNTERED `bid`
  // is the older number and `counterFee` the newer; on RE_OFFERED it is the
  // other way round. The copy in each block says which is which rather than
  // leaving the reader to infer it.
  get quotedAmount(): number {
    return this.product?.proposal?.bid ?? 0;
  }

  // The PA's own counter (mock B3). Distinct from `bid` above — `counterFee`
  // is set only by a counter-offer submission. It is their standing ask on
  // COUNTERED and their previous ask on RE_OFFERED, and either way it is what
  // the counter dialog re-opens seeded with.
  get counterFeeValue(): number | null {
    return this.product?.proposal?.counterFee ?? null;
  }
  // Normalized to `yyyy-MM-dd` for the dialog's native `type="date"` input —
  // a persisted value round-trips as a full ISO datetime string, which a date
  // input silently refuses to display.
  get counterCompleteBy(): string | null {
    const raw = this.product?.proposal?.counterCompleteBy;
    if (!raw) {
      return null;
    }
    const parsed = new Date(raw);
    return isNaN(parsed.getTime()) ? null : parsed.toISOString().slice(0, 10);
  }
  get counterNote(): string | null {
    return this.product?.proposal?.counterNote ?? null;
  }
  get counteredAt(): any {
    return this.product?.proposal?.counteredAt ?? null;
  }
  get quotedTurnTime(): number {
    return this.product?.proposal?.proposedTurnTime ?? 0;
  }

  // OFFERED due date. An offer from CREtelligent carries a delivery/due date
  // rather than a vendor-proposed turn time (that's an INVITATION concept) —
  // mirrors info-table-item.component's
  // `isDesiredByDate ? desiredByDate : data.proposal.proposedDeliverDate`.
  get offerDueDate(): Date | null {
    return this.isDesiredByDate
      ? this.desiredByDate
      : this.product?.proposal?.proposedDeliverDate ?? null;
  }

  // ---------------------------------------------------------------------------
  // testids — flat composite. Base is `site-<num>-product-<title-slug>`;
  // controls append a stable suffix (Task 8 owns cross-row dedupe at the
  // site-card scope).
  // ---------------------------------------------------------------------------
  get testid(): string {
    if (this.baseTestId) {
      return this.baseTestId;
    }
    const num = this.siteNumber != null ? `${this.siteNumber}` : 'x';
    const title = slugify(this.product?.title || 'product') || 'product';
    return `site-${num}-product-${title}`;
  }
  tid(suffix: string): string {
    return `${this.testid}-${suffix}`;
  }
  // Kebab-case the camelCase StageKey so the segment testid matches the app's
  // kebab convention (tid() appends suffixes raw; every other suffix is written
  // kebab, but the StageKey union is camelCase).
  segmentTestid(stage: ProductStage): string {
    const kebab = stage.key.replace(/([a-z])([A-Z])/g, '$1-$2').toLowerCase();
    return this.tid(`stepper-segment-${kebab}`);
  }

  // ---------------------------------------------------------------------------
  // INVITATION — inline quote form.
  // ---------------------------------------------------------------------------
  // Typing mirrors the draft into the shared cache on EVERY keystroke, while
  // the server write stays on blur (savePrice / saveTurnTime below).
  //
  // Two responsibilities that used to be fused into blur alone. The banner's
  // bulk buttons in `project-group` count off `project.raw.sites` — the shared
  // cache — so with the mirror on blur only, this row's own Submit Quote lit
  // up on the first keystroke (it reads the local `bidAmount`) while "Submit
  // All Quotes" stayed stale until the vendor clicked or tabbed away.
  //
  // Mirroring per keystroke is cheap and safe: `patchProposalDraftInCache` is
  // an in-place `Object.assign` that deliberately does NOT re-emit — a
  // re-emission would rebuild the list and interrupt the typing (see the
  // memo comment in `project-group.component.ts`, whose fingerprint cache key
  // exists precisely so an in-place patch is still noticed).
  //
  // One handler per field, rather than one shared handler patching both: the
  // savers spread `...this.product.proposal` into the PUT payload, so a
  // both-field patch would start sending `proposedTurnTime: 0` (the `?? 0`
  // seed) on a price-only edit of a proposal that carries no turn time. It
  // would also mirror an inert `turnTime` under `isDesiredByDate`, where that
  // input is not rendered at all.
  onPriceChange(): void {
    if (this.productLocked) {
      return;
    }
    this.recomputeSubmitDisabled();
    this.projectService.patchProposalDraftInCache(this.product.proposal?.id, {
      bid: this.bidAmount,
    });
  }

  onTurnTimeChange(): void {
    if (this.productLocked) {
      return;
    }
    this.recomputeSubmitDisabled();
    this.projectService.patchProposalDraftInCache(this.product.proposal?.id, {
      proposedTurnTime: this.turnTime,
    });
  }

  private recomputeSubmitDisabled(): void {
    this.submitBidDisabled = !(
      this.bidAmount > 0 &&
      (this.turnTime > 0 || !!this.proposedDeliverDate || this.isDesiredByDate)
    );
  }

  // Persist the typed price as a draft. The unchanged-value skip is the one
  // proposal-details.saveBidAmount has, rebased onto `persistedBid` (see the
  // field's comment). No list refresh here so typing isn't interrupted; the
  // authoritative refresh happens on submit.
  savePrice(): void {
    if (this.productLocked) {
      return;
    }
    if (this.bidAmount !== this.persistedBid) {
      this.proposalService.saveProposal({
        ...this.product.proposal,
        bid: this.bidAmount,
      });
      this.persistedBid = this.bidAmount;
      // Mirror the draft into the shared cache so it survives a navigate-away/
      // return (which replays the cached emission) and a sibling submit merge.
      // `onPriceChange` has already done this for anything typed; kept here so
      // the cache is still correct for a programmatic change to `bidAmount`
      // that never fired an input event.
      this.projectService.patchProposalDraftInCache(this.product.proposal?.id, {
        bid: this.bidAmount,
      });
    }
  }

  saveTurnTime(): void {
    if (this.productLocked) {
      return;
    }
    if (this.turnTime !== this.persistedTurnTime) {
      this.proposalService.saveProposal({
        ...this.product.proposal,
        proposedTurnTime: this.turnTime,
      });
      this.persistedTurnTime = this.turnTime;
      this.projectService.patchProposalDraftInCache(this.product.proposal?.id, {
        proposedTurnTime: this.turnTime,
      });
    }
  }

  // Submit quote → open the existing bid-dialog (requirements acknowledgement +
  // confirmation), which calls ProposalService.submitBid and refreshes the list
  // via ProjectService.updateProjectDataSubjectWithSingleSite.
  submitQuote(): void {
    if (this.productLocked || this.submitBidDisabled) {
      return;
    }
    const quotedSite = {
      ...this.product.siteDetail,
      proposal: {
        ...this.product.proposal,
        bid: this.bidAmount,
        proposedTurnTime: this.turnTime,
      },
    };
    this.writeProjectDetail = this.buildProjectDetail(quotedSite);
    this.bidActive = true;
  }

  toggleBidModal(): void {
    this.bidActive = !this.bidActive;
  }

  // ---------------------------------------------------------------------------
  // OFFERED — accept at the offered price (existing accept-offered dialog).
  // ---------------------------------------------------------------------------
  openAcceptOffer(): void {
    if (this.productLocked) {
      return;
    }
    this.writeProjectDetail = this.buildProjectDetail(this.product.siteDetail);
    this.isOfferAcceptedActive = true;
  }
  toggleOfferAcceptedModal(): void {
    this.isOfferAcceptedActive = !this.isOfferAcceptedActive;
  }
  // Single-offer accept succeeded — close the accept dialog and open the
  // page-hosted confirmation via ModalService (mirrors proposal-details.
  // acceptOffer, but page-level so it survives the list refresh the accept
  // dialog already pushed, which destroys this component).
  onAcceptSingleOffer(): void {
    this.toggleOfferAcceptedModal();
    this.modalService.toggleOpenOfferAcceptedModal();
  }

  // ---------------------------------------------------------------------------
  // Counter Price (OFFERED) / Counter Again (RE_OFFERED) — Task B4. The
  // dialog is a pure form (validates fee/note, emits on submit); this row
  // owns the ProposalService call and the list refresh, the same split
  // `decline()` below already uses.
  // ---------------------------------------------------------------------------
  openCounterOffer(): void {
    if (this.productLocked) {
      return;
    }
    this.isCounterActive = true;
  }
  toggleCounterOfferModal(): void {
    this.isCounterActive = !this.isCounterActive;
  }

  counterOffer(submission: CounterOfferSubmission): void {
    if (this.productLocked) {
      return;
    }
    this.lock
      .hold([this.product.proposal?.id], this.proposalService.counterOffer(
        {
          proposalId: this.product.proposal.id,
          counterFee: submission.counterFee,
          counterCompleteBy: submission.counterCompleteBy,
          counterNote: submission.counterNote,
        },
        this.user.id
      ))
      .subscribe({
        next: (response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            this.toastr.error(
              apiResponse.message || 'An error occurred. Please try again.'
            );
            return;
          }
          // `data` here is the persisted Proposal itself (not a wrapped
          // ProjectDetail — unlike acceptOffer/submitBid). A SUCCESS response
          // is not a guarantee the counter stuck: the order-service push
          // happens after this response returns, and a failed push rolls the
          // record back to its pre-counter values and raises an EDIT failure
          // notification instead of changing this response. Rendering
          // whatever `data` actually holds — rather than assuming COUNTERED —
          // is what keeps this row honest either way.
          const proposal = apiResponse.data;
          if (proposal) {
            this.refreshSingleSite({
              ...this.product.siteDetail,
              proposal,
            });
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        },
        error: () => {
          this.toastr.error('An error occurred. Please try again.');
        },
      });
  }

  // ---------------------------------------------------------------------------
  // Decline (INVITATION / OFFERED) and Withdraw (SUBMITTED) — same existing
  // decline flow. `declineInvitation` closes the proposal; on SUBMITTED it is
  // the withdraw path (edit-bid is backend-locked after submission).
  // ---------------------------------------------------------------------------
  openDeclineModal(): void {
    if (this.productLocked) {
      return;
    }
    this.isAtp = false;
    this.isDeclineActive = true;
  }
  toggleCloseDeclineModal(): void {
    this.isDeclineActive = false;
  }

  decline(event: {
    declineReason: string;
    declineOtherReason: string;
    declineComments: string;
  }): void {
    if (this.productLocked) {
      return;
    }
    const updatedProposal = {
      ...this.product.proposal,
      declineReason: event.declineReason,
      declineOtherReason: event.declineOtherReason,
      declineComments: event.declineComments,
    };
    this.lock
      .hold([this.product.proposal?.id], this.proposalService.declineInvitation(updatedProposal))
      .subscribe({ next: (response: any) => {
        const apiResponse = response;
        if (apiResponse.status === 'FAILURE') {
          this.toastr.error(
            apiResponse.message || 'An error occurred. Please try again.'
          );
        } else {
          const proposal = apiResponse.data;
          if (proposal) {
            // Open the page-hosted confirmation BEFORE the refresh so the
            // (page-level) dialog is what shows — refreshing here destroys this
            // component, so an inline confirmation would never render.
            this.modalService.toggleOpenDeclineSuccessModal(
              this.product.siteDetail,
              this.isFixedPriced,
              this.isAtp
            );
            this.refreshSingleSite({
              ...this.product.siteDetail,
              proposal,
            });
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        }
      }, error: () => {
        this.toastr.error('An error occurred. Please try again.');
      } });
  }

  // ---------------------------------------------------------------------------
  // BID_WON — inline ATP sign. Generate the ATP document, then open the
  // existing signatp-dialog (typed-signature + terms + document review). On
  // sign, SiteService.signAtp flips the proposal to ACTIVE and the list
  // refreshes. Reproduces proposal-details.generateATP / signATP / declineATP.
  // ---------------------------------------------------------------------------
  generateATP(): void {
    if (this.productLocked) {
      return;
    }
    this.toastr.info(
      'Please wait a moment while we generate your ATP document. This process may take a few seconds.'
    );
    const projectDetail = this.buildProjectDetail(this.product.siteDetail);
    this.lock
      .hold([this.product.proposal?.id], this.siteService.generateAtp(projectDetail, this.user.id ?? ''))
      .subscribe({ next: (response) => {
        if (response && response.status === 'SUCCESS' && response.data) {
          this.atpDocument = response.data;
          if (this.atpDocument.documentPath) {
            this.atpDocumentPath = this.sanitizer.bypassSecurityTrustResourceUrl(
              this.atpDocument.documentPath
            );
            this.signAtpActive = true;
          } else {
            this.toastr.error(
              'There was an issue with generating your ATP document. Please try again.'
            );
          }
        } else {
          this.toastr.error(
            (response && response.message) ||
              'There was an issue with generating your ATP document. Please try again.'
          );
        }
      }, error: () => {
        this.toastr.error('An error occurred. Please try again.');
      } });
  }

  toggleATPModal(): void {
    this.signAtpActive = !this.signAtpActive;
  }

  signATP(event: { fullName: string }): void {
    if (this.productLocked) {
      return;
    }
    this.toastr.info('Accepting your signed ATP...');
    const projectDetail = this.buildProjectDetail(this.product.siteDetail);
    this.lock
      .hold([this.product.proposal?.id], this.siteService.signAtp(
        {
          projectDetail,
          atpDocument: this.atpDocument,
          atpSignedBy: event.fullName,
        },
        this.user.id ?? ''
      ))
      .subscribe({ next: (response) => {
        // The sign response's `data` is now `{ projectDetail, siteVisits }`.
        //
        // Emit BEFORE the project-detail push below: that refresh drops the
        // signed BID_WON item off this screen and destroys this row, which
        // tears down the (atpSigned) listener chain the visits travel on.
        this.atpSigned.emit(response.data?.siteVisits ?? []);
        const updatedProjectDetail = response.data.projectDetail;
        if (updatedProjectDetail) {
          this.projectService.updateProjectDataSubjectWithProjectDetail(
            updatedProjectDetail
          );
        }
        this.signAtpActive = false;
        // Page-hosted confirmation (dashboard:false) — survives the refresh
        // just pushed, which drops the signed BID_WON item off this screen.
        this.modalService.toggleOpenAtpSignAcceptedModal();
      }, error: () => {
        this.signAtpActive = false;
        this.toastr.error('An error occurred. Please try again.');
      } });
  }

  declineATP(event: {
    declineReason: string;
    declineOtherReason: string;
    declineComments: string;
  }): void {
    if (this.productLocked) {
      return;
    }
    const updatedProposal = {
      ...this.product.proposal,
      declineReason: event.declineReason,
      declineOtherReason: event.declineOtherReason,
      declineComments: event.declineComments,
    };
    const projectDetail = this.buildProjectDetail({
      ...this.product.siteDetail,
      proposal: updatedProposal,
    });
    this.lock
      .hold([this.product.proposal?.id], this.siteService.cancel(this.user.id ?? '', projectDetail))
      .subscribe({ next: (response: any) => {
        const apiResponse = response;
        if (apiResponse.status === 'FAILURE') {
          this.toastr.error(
            apiResponse.message || 'An error occurred. Please try again.'
          );
        } else {
          const updatedProjectDetail = apiResponse.data;
          if (updatedProjectDetail) {
            this.projectService.updateProjectDataSubjectWithProjectDetail(
              updatedProjectDetail
            );
            this.isAtp = true;
            // Page-hosted decline confirmation (isAtp copy) — survives refresh.
            this.modalService.toggleOpenDeclineSuccessModal(
              this.product.siteDetail,
              this.isFixedPriced,
              true
            );
          } else {
            this.toastr.error('An error occurred. Please try again.');
          }
        }
      }, error: () => {
        this.toastr.error('An error occurred. Please try again.');
      } });
  }

  // ---------------------------------------------------------------------------
  // Shared helpers.
  // ---------------------------------------------------------------------------
  // Rebuild the ProjectDetail the legacy write paths posted: the full raw
  // project (banner-level fields the backend/ATP generator read —
  // projectOrderId, tableA, specialQuoteRequirements, rush, …) with a single
  // site — the untouched `siteDetail` (or a caller-supplied quote/decline copy).
  // Built synchronously from the `rawProject` @Input (no async lookup).
  private buildProjectDetail(siteDetail: any): any {
    return {
      ...this.rawProject,
      ...(this.projectId ? { projectId: this.projectId } : {}),
      sites: [siteDetail],
    };
  }

  // Push a single updated site back into ProjectService so the list re-renders,
  // exactly as the legacy paths did (updateProjectDataSubjectWithSingleSite).
  private refreshSingleSite(updatedSiteDetail: any): void {
    this.projectService.updateProjectDataSubjectWithSingleSite(
      this.buildProjectDetail(updatedSiteDetail)
    );
  }
}
